import { CourseListFilters } from "../generated/graphql"
import { safeSessionStorage } from "./safeStorage"
import { safeJsonParse } from "./safeJsonParse"

/**
 * Per-Embed gescopte Persistenz für Kursfilter über sessionStorage.
 *
 * Filter werden pro Embed-Konfiguration (Seed) unter einem deterministisch
 * gehashten Key abgelegt. Der Key wird ausschließlich aus dem Seed abgeleitet
 * (keine Instanz-ID), daraus folgt das Scoping-Verhalten:
 *  - Embeds mit UNTERSCHIEDLICHER Seed-Konfiguration sind isoliert (verschiedene
 *    Keys → kein gemeinsamer Filterzustand).
 *  - IDENTISCH konfigurierte Embeds (z.B. zwei leere Seeds) teilen sich bewusst
 *    denselben Key und damit denselben Filterzustand.
 *  - Identische Embeds finden ihren Zustand über Navigationen hinweg wieder.
 */

export const FILTER_STATE_STORAGE_KEY_PREFIX = "koCourseFilters:"

/**
 * Liste der persistierbaren Filter-Keys (Subset von CourseListFilters).
 * Nur diese Keys werden gelesen, geschrieben und für den Storage-Key gehasht.
 * UI-/Config-Felder (z.B. showFilterMenu) gehören NICHT dazu und sind
 * ohnehin nicht Teil von CourseListFilters.
 */
const PERSISTABLE_FILTER_KEYS: Array<keyof CourseListFilters> = [
    "ids",
    "dayFilter",
    "monthFilter",
    "instructorId",
    "courseCategoryId",
    "courseCategoryIds",
    "courseTypeId",
    "courseTypeIds",
    "locationId",
    "city",
    "showOnlyInWebActive",
    "restrictiveCourses",
    "prerequisiteCourseId",
]

/**
 * Übernimmt nur die bekannten Filter-Keys aus einem beliebigen Objekt.
 * Null/undefined-Werte werden übersprungen.
 */
function pickPersistableFilters(source: Record<string, unknown>): CourseListFilters {
    const picked: Record<string, unknown> = {}
    for (const key of PERSISTABLE_FILTER_KEYS) {
        const value = source[key as string]
        if (value !== null && value !== undefined) {
            picked[key as string] = value
        }
    }
    return picked as CourseListFilters
}

/**
 * Prüft, ob ein Wert ein Plain-Object ist (kein null, kein Array).
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

/**
 * Deterministische, stabile Serialisierung eines Werts.
 * Objekte: Keys alphabetisch sortiert. Arrays: Reihenfolge bleibt erhalten
 * (Filter-Arrays sind bedeutungstragend geordnet, daher kein Sortieren),
 * aber rekursiv stabil serialisiert.
 */
function stableStringify(value: unknown): string {
    if (Array.isArray(value)) {
        return "[" + value.map((item) => stableStringify(item)).join(",") + "]"
    }
    if (isPlainObject(value)) {
        const keys = Object.keys(value).sort()
        return (
            "{" +
            keys.map((key) => JSON.stringify(key) + ":" + stableStringify(value[key])).join(",") +
            "}"
        )
    }
    return JSON.stringify(value) ?? "null"
}

/**
 * djb2 String-Hash — reiner deterministischer Hash ohne Zeit-/Zufallsanteil.
 * Rückgabe als unsigned-Hex-String.
 */
function djb2Hash(input: string): string {
    let hash = 5381
    for (let i = 0; i < input.length; i++) {
        // hash * 33 + charCode, in 32-Bit gehalten
        hash = (hash * 33) ^ input.charCodeAt(i)
    }
    // In unsigned 32-Bit umwandeln und als Hex zurückgeben
    return (hash >>> 0).toString(16)
}

/**
 * Baut einen deterministischen, pro Seed stabilen Storage-Key.
 * Es werden nur die gesetzten, persistierbaren Filter-Keys berücksichtigt,
 * alphabetisch sortiert und stabil serialisiert, dann via djb2 gehasht.
 *
 * - Identische Seeds → identischer Key.
 * - Unterschiedliche Seeds → unterschiedlicher Key.
 * - Leerer/undefined Seed → stabiler Default-Suffix (Hash des leeren Objekts).
 */
export function buildFilterStorageKey(seed: CourseListFilters | undefined): string {
    const source: Record<string, unknown> = isPlainObject(seed) ? seed : {}
    const picked = pickPersistableFilters(source)
    // pickPersistableFilters liefert bereits nur bekannte, gesetzte Keys;
    // stableStringify sortiert die Keys alphabetisch.
    const serialized = stableStringify(picked)
    return FILTER_STATE_STORAGE_KEY_PREFIX + djb2Hash(serialized)
}

/**
 * Lädt persistierte Filter und sanitisiert defensiv: nur bekannte Keys werden
 * übernommen, das Ergebnis ist immer ein Plain-Object. Bei Garbage → {}.
 */
export function loadStoredFilters(key: string): CourseListFilters {
    let raw: string | null = null
    try {
        raw = safeSessionStorage.getItem(key)
    } catch {
        // Storage blockiert/instabil — still degradieren
        return {}
    }

    const parsed = safeJsonParse<CourseListFilters>(raw) ?? {}
    if (!isPlainObject(parsed)) {
        return {}
    }
    return pickPersistableFilters(parsed)
}

/**
 * Persistiert nur die bekannten Filter-Keys. Wirft niemals.
 */
export function persistFilters(key: string, filters: CourseListFilters): void {
    try {
        const source: Record<string, unknown> = isPlainObject(filters) ? filters : {}
        const picked = pickPersistableFilters(source)
        safeSessionStorage.setItem(key, JSON.stringify(picked))
    } catch {
        // Serialisierung oder Storage fehlgeschlagen — still degradieren
    }
}

/**
 * Entfernt den persistierten Filterzustand für den gegebenen Key.
 */
export function clearStoredFilters(key: string): void {
    try {
        safeSessionStorage.removeItem(key)
    } catch {
        // Storage blockiert/instabil — still degradieren
    }
}
